import React from 'react';
import { Table } from './Table';
import type { TableProps } from './Table';
import { MobileTableCard } from './MobileTableCard';
import { useIsMobile } from '../../hooks';

export interface ResponsiveTableProps<T> extends TableProps<T> {
  /**
   * Optional custom render function for mobile cards
   * If not provided, uses generic MobileTableCard
   */
  renderMobileCard?: (row: T) => React.ReactNode;
  /**
   * Message shown when data is empty on mobile
   * Falls back to emptyMessage if not provided
   */
  mobileEmptyMessage?: string;
}

/**
 * Responsive table component that adapts to screen size
 * - Desktop (≥ md: 768px): Renders as standard table
 * - Mobile (< md: 768px): Renders as stacked cards
 * - Preserves all Table functionality (sorting, actions, onClick)
 * - Supports custom mobile card rendering via renderMobileCard prop
 *
 * @example
 * ```tsx
 * // Generic mobile cards
 * <ResponsiveTable
 *   columns={columns}
 *   data={users}
 *   onRowClick={handleClick}
 *   renderActions={(row) => <Button>Edit</Button>}
 * />
 *
 * // Custom mobile cards
 * <ResponsiveTable
 *   columns={columns}
 *   data={campaigns}
 *   renderMobileCard={(campaign) => <CampaignMobileCard campaign={campaign} />}
 * />
 * ```
 */
export function ResponsiveTable<T>({
  columns,
  data,
  emptyMessage = 'No data available.',
  mobileEmptyMessage,
  onRowClick,
  renderActions,
  renderMobileCard,
  className,
}: ResponsiveTableProps<T>) {
  const isMobile = useIsMobile();

  // Desktop view: render standard table
  if (!isMobile) {
    return (
      <Table
        columns={columns}
        data={data}
        emptyMessage={emptyMessage}
        onRowClick={onRowClick}
        renderActions={renderActions}
        className={className}
      />
    );
  }

  // Mobile view: render as cards
  const displayEmptyMessage = mobileEmptyMessage || emptyMessage;

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 text-sm">
        {displayEmptyMessage}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data.map((row, index) => {
        // Use custom mobile card if provided
        if (renderMobileCard) {
          return <div key={index}>{renderMobileCard(row)}</div>;
        }

        // Otherwise use generic MobileTableCard
        return (
          <MobileTableCard
            key={index}
            data={row}
            columns={columns}
            onRowClick={onRowClick}
            renderActions={renderActions}
          />
        );
      })}
    </div>
  );
}

ResponsiveTable.displayName = 'ResponsiveTable';
